import type { FC, HTMLAttributes } from "react";
import { useCallback, useContext, useEffect, useRef } from "react";
import type { Placement } from "@react-types/overlays";
import { ChevronSelectorVertical, Home03, LogOut01, User01 } from "@untitledui/icons";
import { useFocusManager } from "react-aria";
import type { DialogProps as AriaDialogProps } from "react-aria-components";
import {
    Button as AriaButton,
    Dialog as AriaDialog,
    DialogTrigger as AriaDialogTrigger,
    OverlayTriggerStateContext,
    Popover as AriaPopover
} from "react-aria-components";
import { prefixes, useTypedTranslation } from "@/locales";
import { useNavigate } from "react-router";
import { AvatarLabelGroup } from "@/components/_untitled/base/avatar/avatar-label-group.tsx";
import { RadioButtonBase } from "@/components/_untitled/base/radio-buttons/radio-buttons.tsx";
import { useBreakpoint } from "@/hooks/use-breakpoint.ts";
import { cx } from "@/utils/cx.ts";
import type { AuctionHouseOption } from "@/types/auction-house";
import { ROUTES } from "@/config/routes";
import { getAssetUrl } from "@/utils/assetUrl";
import { getInitials } from "@/utils/avatar";

export const NavAccountMenu = ({
                                   className,
                                   selectedAuctionHouseId,
                                   auctionHouses = [],
                                   onAuctionHouseChange,
                                   onSignOut,
                                   ...dialogProps
                               }: AriaDialogProps & {
    className?: string;
    selectedAuctionHouseId?: string;
    auctionHouses?: AuctionHouseOption[];
    onAuctionHouseChange?: (auctionHouseId: string) => void;
    onSignOut?: () => void;
}) => {
    const [tAccountMenu] = useTypedTranslation(prefixes('header.accountMenu'));
    const navigate = useNavigate();
    const focusManager = useFocusManager();
    const dialogRef = useRef<HTMLDivElement>(null);
    const state = useContext(OverlayTriggerStateContext);

    const onKeyDown = useCallback(
        (e: KeyboardEvent) => {
            switch (e.key) {
                case "ArrowDown":
                    focusManager?.focusNext({ tabbable: true, wrap: true });
                    break;
                case "ArrowUp":
                    focusManager?.focusPrevious({ tabbable: true, wrap: true });
                    break;
            }
        },
        [focusManager],
    );

    useEffect(() => {
        const element = dialogRef.current;
        if (element) {
            element.addEventListener("keydown", onKeyDown);
        }

        return () => {
            if (element) {
                element.removeEventListener("keydown", onKeyDown);
            }
        };
    }, [onKeyDown]);

    return (
        <AriaDialog
            { ...dialogProps }
            ref={ dialogRef }
            className={ cx("w-66 rounded-xl bg-secondary_alt shadow-lg ring ring-secondary_alt outline-hidden", className) }
        >
            <div className="rounded-xl bg-primary ring-1 ring-secondary">
                <div className="flex flex-col gap-0.5 py-1.5">
                    <NavAccountCardMenuItem
                        label={ tAccountMenu('viewProfile') }
                        icon={ User01 }
                        shortcut="⌘K->P"
                        onClick={ () => {
                            navigate(ROUTES.ADMIN_SETTINGS);
                            state?.close();
                        } }
                    />
                    <NavAccountCardMenuItem
                        label={ tAccountMenu('switchToRegularMode') }
                        icon={ Home03 }
                        onClick={ () => {
                            navigate(ROUTES.HOME);
                            state?.close();
                        } }
                    />
                </div>
                { auctionHouses.length > 1 && (
                    <div className="flex flex-col gap-0.5 border-t border-secondary py-1.5">
                        <div className="px-3 pt-1.5 pb-1 text-xs font-semibold text-tertiary">
                            { tAccountMenu('switchAuctionHouse') }
                        </div>

                        <div className={ cx(
                            "flex flex-col gap-0.5 px-1.5",
                            auctionHouses.length > 4 && "max-h-80 overflow-y-auto"
                        ) }>
                            { auctionHouses.map((house) => (
                                <button
                                    key={ house.id }
                                    onClick={ () => onAuctionHouseChange?.(house.id) }
                                    className={ cx(
                                        "relative w-full cursor-pointer rounded-md px-2 py-1.5 text-start outline-focus-ring hover:bg-primary_hover focus:z-10 focus-visible:outline-2 focus-visible:outline-offset-2",
                                        house.id === selectedAuctionHouseId && "bg-primary_hover",
                                    ) }
                                >
                                    <AvatarLabelGroup
                                        size="md"
                                        src={ getAssetUrl(house.icon) || undefined }
                                        title={ house.name }
                                        subtitle=""
                                    />
                                    <RadioButtonBase isSelected={ house.id === selectedAuctionHouseId }
                                                     className="absolute top-2 end-2"/>
                                </button>
                            )) }
                        </div>
                    </div>
                ) }
            </div>

            <div className="pt-1 pb-1.5">
                <NavAccountCardMenuItem
                    data-testid="logout-button"
                    label={ tAccountMenu('signOut') }
                    icon={ LogOut01 }
                    onClick={ onSignOut }
                />
            </div>
        </AriaDialog>
    );
};

const NavAccountCardMenuItem = ({
                                    icon: Icon,
                                    label,
                                    shortcut,
                                    ...buttonProps
                                }: {
    icon?: FC<{ className?: string }>;
    label: string;
    shortcut?: string;
} & HTMLAttributes<HTMLButtonElement>) => {
    return (
        <button { ...buttonProps }
                className={ cx("group/item w-full cursor-pointer px-1.5 focus:outline-hidden", buttonProps.className) }>
            <div
                className={ cx(
                    "flex w-full items-center justify-between gap-3 rounded-md p-2 group-hover/item:bg-primary_hover",
                    // Focus styles.
                    "outline-focus-ring group-focus-visible/item:outline-2 group-focus-visible/item:outline-offset-2",
                ) }
            >
                <div className="flex gap-2 text-sm font-semibold text-secondary group-hover/item:text-secondary_hover">
                    { Icon && <Icon className="size-5 text-fg-quaternary"/> } { label }
                </div>

                { shortcut && (
                    <kbd
                        className="flex rounded px-1 py-px font-body text-xs font-medium text-tertiary ring-1 ring-secondary ring-inset">{ shortcut }</kbd>
                ) }
            </div>
        </button>
    );
};

export const NavAccountCard = ({
                                   popoverPlacement,
                                   user,
                                   auctionHouses = [],
                                   selectedAuctionHouseId,
                                   onAuctionHouseChange,
                                   onSignOut,
                               }: {
    popoverPlacement?: Placement;
    user?: { name: string; email: string; avatar: string };
    auctionHouses?: AuctionHouseOption[];
    selectedAuctionHouseId?: string;
    onAuctionHouseChange?: (auctionHouseId: string) => void;
    onSignOut?: () => void;
}) => {
    const triggerRef = useRef<HTMLDivElement>(null);
    const isDesktop = useBreakpoint("lg");

    if (!user) {
        return null;
    }

    const initials = getInitials(user.name);

    return (
        <div ref={ triggerRef }
             className="relative flex items-center gap-3 rounded-xl p-3 ring-1 ring-secondary ring-inset">
            <AvatarLabelGroup
                size="md"
                src={ user.avatar || null }
                alt={ user.name }
                initials={ initials }
                className='text-white'
                title={ user.name }
                subtitle={ user.email }
                status="online"
                contrastBorder={ false }
            />

            <div className="absolute top-1.5 end-1.5">
                <AriaDialogTrigger>
                    <AriaButton data-testid="user-profile-button"
                                className="flex cursor-pointer items-center justify-center rounded-md p-1.5 text-fg-quaternary outline-focus-ring transition duration-100 ease-linear hover:bg-primary_hover hover:text-fg-quaternary_hover focus-visible:outline-2 focus-visible:outline-offset-2 pressed:bg-primary_hover pressed:text-fg-quaternary_hover">
                        <ChevronSelectorVertical className="size-4 shrink-0"/>
                    </AriaButton>
                    <AriaPopover
                        placement={ popoverPlacement ?? (isDesktop ? "right bottom" : "top end") }
                        triggerRef={ triggerRef }
                        offset={ 8 }
                        className={ ({ isEntering, isExiting }) =>
                            cx(
                                "will-change-transform",
                                isEntering &&
                                "duration-150 ease-out animate-in fade-in placement-right:origin-start placement-right:slide-in-from-start-0.5 placement-top:origin-bottom placement-top:slide-in-from-bottom-0.5 placement-bottom:origin-top placement-bottom:slide-in-from-top-0.5",
                                isExiting &&
                                "duration-100 ease-in animate-out fade-out placement-right:origin-start placement-right:slide-out-to-start-0.5 placement-top:origin-bottom placement-top:slide-out-to-bottom-0.5 placement-bottom:origin-top placement-bottom:slide-out-to-top-0.5",
                            )
                        }
                    >
                        <NavAccountMenu
                            selectedAuctionHouseId={ selectedAuctionHouseId }
                            auctionHouses={ auctionHouses }
                            onAuctionHouseChange={ onAuctionHouseChange }
                            onSignOut={ onSignOut }
                        />
                    </AriaPopover>
                </AriaDialogTrigger>
            </div>
        </div>
    );
};
